import { useCallback, useState, useEffect } from 'react'
import useEmblaCarousel, { EmblaCarouselType } from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from '@tamagui/lucide-icons'
import { Button, ButtonProps } from '../primitive'
import { shadowStandard } from '../styles'

type CarouselType = {
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  scrollNext: () => void
  scrollPrev: () => void
  emblaRef: any
}

export const useCarousel = (): CarouselType => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 'auto',
    containScroll: 'keepSnaps',
    skipSnaps: true,
  })
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
  }, [emblaApi, onSelect])
  return { prevBtnDisabled, nextBtnDisabled, scrollNext, scrollPrev, emblaRef }
}

interface CarouselButtonsProps {
  carousel: CarouselType
  buttonProps?: ButtonProps
}

export const CarouselButtons = ({ carousel, buttonProps }: CarouselButtonsProps) => {
  return (
    <>
      <Button
        theme="whiteGray"
        br="$3"
        pos="absolute"
        top="50%"
        y="-50%"
        w={50}
        h={50}
        p="$0"
        left={0}
        icon={<ChevronLeft size={24} />}
        onPress={carousel.scrollPrev}
        disabled={carousel.prevBtnDisabled}
        opacity={carousel.prevBtnDisabled ? 0 : 1}
        ml="$2"
        {...shadowStandard}
        {...buttonProps}
        aria-label="Previous slide"
      />
      <Button
        theme="whiteGray"
        br="$3"
        pos="absolute"
        top="50%"
        y="-50%"
        w={50}
        h={50}
        p="$0"
        right={0}
        icon={<ChevronRight size={24} />}
        onPress={carousel.scrollNext}
        disabled={carousel.nextBtnDisabled}
        opacity={carousel.nextBtnDisabled ? 0 : 1}
        mr="$2"
        {...shadowStandard}
        {...buttonProps}
        aria-label="Next slide"
      />
    </>
  )
}
